import type React from "react";
import type { ButtonProps } from "./components/button";

/**
 * Shape a custom link component must accept to be injected as `LinkComponent`.
 * Lets a consuming app override how same-domain links render (e.g. a native
 * anchor for routes whose href is mutated at click time).
 */
export type LinkComponentProps = {
  href: string;
  className?: string;
  onClick?: () => void;
  "aria-label"?: string;
  children: React.ReactNode;
};

/**
 * Accessible labels for the header's own chrome. Nav and banner *content*
 * already arrives translated via `fixtureData`; these are the strings the
 * header renders itself, so a localised app must inject them — packages/ui is
 * outside every app's Lingui extraction and cannot translate them in place.
 */
export type HeaderLabels = {
  openNavigationMenu: string;
  closeNavigationMenu: string;
  closeBanner: string;
};

export const defaultHeaderLabels: HeaderLabels = {
  openNavigationMenu: "Open navigation menu",
  closeNavigationMenu: "Close navigation menu",
  closeBanner: "Close banner",
};

/**
 * Header layout variant. `"default"` is the current production nav and the
 * default for every consumer. `"floating"` opts into the redesigned Fin nav
 * (compact floating dropdown panels, tiered links, prose/full-width rows).
 * New variants must never change how `"default"` renders.
 */
export type HeaderVariant = "default" | "floating";

/**
 * Width at which the floating variant swaps the drawer for the full desktop
 * row. `"default"` (1230px) fits English and Japanese;
 * `"wide"` (1536px) is for locales whose labels overflow the bar well past
 * 1230 — French needs 1431px. Ignored by the `"default"` variant.
 */
export type DesktopBreakpoint = "default" | "wide";

export type NavLink = {
  href: string;
  title?: string;
  description?: string;
  icon?: React.ReactNode;
  image?: React.ReactNode;
  external?: boolean;
  /** Override the text sent in the navItemClick analytics event (defaults to title) */
  analyticsText?: string;
  /** Base identifier for this row; each surface appends its own suffix. */
  analyticsId: string;
  /**
   * Visual weight within a floating-variant dropdown section. `"primary"`
   * renders a prominent lead item; `"secondary"` (default) renders the compact
   * row used inside keyline groups. Ignored by the `"default"` variant.
   */
  tier?: "primary" | "secondary";
  /**
   * Floating variant only: suppress `description` on desktop while still
   * showing it in the mobile drawer. Lets a single link definition render
   * label-only in a dense desktop column but keep its description on mobile,
   * so `links` and `mobileLinks` can share one source of truth.
   */
  descriptionMobileOnly?: boolean;
};

/** A grid of links laid out across N columns within a section. */
export type NavLinkGrid = {
  columns: number;
  links: NavLink[];
};

/**
 * A prose paragraph mixing plain text and inline links, e.g.
 * "Supports: Voice, Chat, Email and more". Floating variant only.
 * Discriminated by the `prose` key.
 */
export type NavLinkProse = {
  prose: Array<
    | string
    | {
        label: string;
        href: string;
        external?: boolean;
        analyticsText?: string;
        /** Base identifier for this segment; each surface appends its own suffix. */
        analyticsId: string;
      }
  >;
};

/**
 * A full-width row spanning the whole dropdown (e.g. a "recent product
 * updates" footer bar or a cross-brand banner), with a hover-fill accent.
 * Floating variant only. Discriminated by the `fullWidthRow` key.
 */
export type NavLinkFullWidthRow = {
  fullWidthRow: true;
  label: string;
  href: string;
  description?: string;
  icon?: React.ReactNode;
  external?: boolean;
  analyticsText?: string;
  /** Base identifier for this row; each surface appends its own suffix. */
  analyticsId: string;
  /** Hover-fill accent colour token. Defaults to a neutral fill. */
  accent?: "chalk" | "fin" | "helpdesk" | "pioneer";
  /** `"large"` gives the row banner-sized type and padding. */
  size?: "default" | "large";
};

export type NavLinkSection = {
  title?: string;
  links: (NavLink | NavLinkGrid | NavLinkProse | NavLinkFullWidthRow)[];
  /**
   * Render this section's secondary links inside an animated keyline group
   * (floating variant only). No effect on the `"default"` variant.
   */
  keyline?: boolean;
  /**
   * Floating variant only: widen the vertical gap between this section's rows
   * (used by sparse columns like Customers to match the prototype). Desktop
   * dropdown only; ignored by the mobile drawer and the `"default"` variant.
   */
  spacious?: boolean;
};

export type NavItem =
  | {
      type: "link";
      label: string;
      href: string;
      /** Override the text sent in the navItemClick analytics event (defaults to label) */
      analyticsText?: string;
      analyticsId: string;
    }
  | {
      type: "smallLink";
      label: string;
      href: string;
      /** Override the text sent in the navItemClick analytics event (defaults to label) */
      analyticsText?: string;
      analyticsId: string;
    }
  | ({
      type: "button";
      label: string;
      href: string;
      tooltip?: string;
      /** Override the text sent in the navItemClick analytics event (defaults to label) */
      analyticsText?: string;
      analyticsId: string;
      /**
       * Renders only in the collapsed mobile top bar (below the tablet
       * breakpoint where that bar shows every `secondary` item); hidden from
       * both the desktop nav and the tablet-width collapsed bar. Lets a
       * `secondary` CTA differ on phone-width screens without touching
       * desktop, e.g. swapping the persistent mobile CTA independently of
       * the desktop button row.
       */
      mobileOnly?: boolean;
    } & Partial<
      Pick<ButtonProps, "variant" | "size" | "isExternal" | "className">
    >)
  | {
      type: "dropdown";
      label: string;
      links: NavLinkSection[][];
      dropdownAlign?: "start" | "center";
      dropdownClassName?: string;
      /**
       * Floating-variant mobile drawer only: a flat, bespoke re-grouping of
       * this dropdown's links into vertical accordion sections. The desktop
       * columns don't map cleanly onto the mobile layout, so when present the
       * floating mobile accordion renders these sections instead of deriving
       * them from `links`. Ignored by the `"default"` variant and desktop.
       */
      mobileLinks?: NavLinkSection[];
    }
  | {
      type: "customDropdown";
      label: string;
      customSubmenu: React.ReactNode;
    }
  | {
      type: "iconButton";
      label: string;
      href: string;
      icon: React.ReactNode;
      /** Override the text sent in the navItemClick analytics event (defaults to label) */
      analyticsText?: string;
      analyticsId: string;
    }
  | {
      type: "custom";
      customItem: React.ReactNode;
    }
  | {
      type: "customWithDropdown";
      customItem: React.ReactNode;
      links: NavLinkSection[][];
    };

/**
 * Rich cross-brand hover card shown on the cross-site link (floating variant).
 * Dots + accent are driven by `accentColor`, which should be the *destination*
 * brand's colour (Fin nav promotes Intercom → "helpdesk"; Intercom nav
 * promotes Fin → "fin"). The title renders `titleLead` then `titleAccent` in a
 * muted colour. Falls back to the plain `crossSiteLink` when absent.
 * When `ctaLabel`/`ctaHref` are omitted the card is compact (no button),
 * matching the prototype's default cross-site presence.
 */
export type CrossSiteCard = {
  titleLead?: string;
  titleAccent?: string;
  description: string;
  ctaLabel?: string;
  ctaHref?: string;
  accentColor: "fin" | "helpdesk";
};

export type HeaderNavigation = {
  primary: NavItem[];
  secondary: NavItem[];
  crossSiteLink?: NavItem;
  /** Floating variant only; enhances `crossSiteLink` into a hover card. */
  crossSiteCard?: CrossSiteCard;
  logo: {
    icon: React.ReactNode;
    href: string;
    label: string;
    /** Override the text sent in the navItemClick analytics event (defaults to label) */
    analyticsText?: string;
    /** Floating variant only; height of the desktop logo row. Defaults to `h-10`. */
    rowClassName?: string;
  };
  banner?: {
    text: string;
    textMobile?: string;
    href: string;
    linkLabel: string;
    target: string;
    disableMarquee?: boolean;
    disableCloseOnLinkClick?: boolean;
    /** Override the text sent in the navItemClick analytics event (defaults to `${text} ${linkLabel}`) */
    analyticsText?: string;
    /** Light grey bar with off-black text. Defaults to off-black with white text. */
    theme?: "light";
    /** Decorative images pinned to each end of the bar on desktop, and one full-width image on mobile. */
    backgroundImages?: { left: string; right: string; mobile: string };
  };
};

export type MobileMenuNavItem =
  | {
      type: "dropdown";
      label: string;
      links: NavLinkSection[][];
      /** Floating-variant mobile drawer only; see `NavItem` dropdown. */
      mobileLinks?: NavLinkSection[];
    }
  | {
      type: "link";
      label: string;
      href: string;
      /** Override the text sent in the navItemClick analytics event (defaults to label) */
      analyticsText?: string;
      analyticsId: string;
    }
  | {
      type: "smallLink";
      label: string;
      href: string;
      /** Override the text sent in the navItemClick analytics event (defaults to label) */
      analyticsText?: string;
      analyticsId: string;
      /** Appends a trailing arrow marking the link as leaving this site. */
      isExternal?: boolean;
    }
  | ({
      type: "button";
      label: string;
      href: string;
      /** Override the text sent in the navItemClick analytics event (defaults to label) */
      analyticsText?: string;
      analyticsId: string;
    } & Partial<
      Pick<ButtonProps, "variant" | "size" | "isExternal" | "className">
    >)
  | {
      type: "custom";
      customItem: React.ReactNode;
    };

export type MobileMenuNavigation = {
  primary: MobileMenuNavItem[];
  secondary: MobileMenuNavItem[];
  tertiary?: MobileMenuNavItem[];
  crossSiteLink?: {
    label: string;
    href: string;
    /** Override the text sent in the navItemClick analytics event (defaults to label) */
    analyticsText?: string;
  };
};

/**
 * Event detail for the 'navItemClick' custom event
 * Dispatched when a navigation item is clicked
 */
export type NavItemClickEventDetail = {
  text: string;
  destination: string;
  location: string;
  analyticsId: string;
  /** Present when analyticsText was used to override the default text */
  analyticsText?: string;
};

/**
 * Custom event type for navigation clicks
 * Usage in consuming apps:
 *
 * window.addEventListener('navItemClick', (event: NavItemClickEvent) => {
 *   const { text, destination, location } = event.detail;
 *   // Your analytics code here
 * });
 */
export type NavItemClickEvent = CustomEvent<NavItemClickEventDetail>;
